<script>
import TreeNode from "@/components/tabs/TreeNode.vue";
import Caching from "@/services/Caching";

export default {
  props: ['keyData'],
  components: { TreeNode },
  data() {
    return {
      keyDetails: null,
      detailsParsed: null,
      detailsLoading: false,
      detailsError: null,
      searchQuery: '',
      showOnlyMatches: false,
      rootExpanded: true,
      showModal: false
    };
  },

  methods: {

    closeModal() {
      this.showModal = false
    },

    async getKeyDetails() {
      this.detailsLoading = true;
      this.detailsError = null;
      this.keyDetails = null;
      this.detailsParsed = null;
      try {
        const response = await Caching.getKeyDetails(this.keyData);
        const payload = response && response.data ? response.data.data : null;
        this.keyDetails = payload;
        try {
          this.detailsParsed = this.deepParseJsonStrings(payload.value);
        } catch {
          this.detailsParsed = null;
        }
      } catch (err) {
        this.detailsError = err.response?.data?.error || err.message;
      } finally {
        this.detailsLoading = false;
      }
    },

    deepParseJsonStrings(value, depth = 0) {
      if (depth > 4) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return this.deepParseJsonStrings(parsed, depth + 1);
        } catch {
          return value;
        }
      }
      if (Array.isArray(value)) {
        return value.map(v => this.deepParseJsonStrings(v, depth + 1));
      }
      if (value && typeof value === 'object') {
        const out = {};
        Object.keys(value).forEach(k => {
          out[k] = this.deepParseJsonStrings(value[k], depth + 1);
        });
        return out;
      }
      return value;
    },

    pretty(obj) {
      try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
    },

    escapeHtml(str) {
      return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
    },

    highlight(text, query) {
      if (!query) return this.escapeHtml(text);
      const esc = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(esc, 'gi');
      return this.escapeHtml(text).replace(re, m => `<mark>${m}</mark>`);
    },

    buildFilteredJson(obj, query) {
      // Return a pruned copy containing only matching branches
      if (!query) return obj;
      const q = query.toLowerCase();
      const walk = (val) => {
        if (val == null) return null;
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
          return String(val).toLowerCase().includes(q) ? val : null;
        }
        if (Array.isArray(val)) {
          const arr = val.map(walk).filter(v => v !== null);
          return arr.length ? arr : null;
        }
        if (typeof val === 'object') {
          const out = {};
          Object.keys(val).forEach(k => {
            const child = walk(val[k]);
            if (child !== null || k.toLowerCase().includes(q)) {
              out[k] = child === null ? val[k] : child;
            }
          });
          return Object.keys(out).length ? out : null;
        }
        return null;
      };
      const pruned = walk(obj);
      return pruned ?? {};
    },

    showData(){
      this.getKeyDetails()
    }
  }
}
</script>

<template>
  <b-modal @shown="showData" v-model="showModal" id="viewRedisKeyDetails" title="View Redis Key Details" title-class="font-18" size="xl" centered>
    <div class="card">
      <div class="card-body">
        <div class="row align-items-center mb-3">
          <div class="col-sm-12 col-md-8">
            <div class="input-group">
              <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
              <input v-model="searchQuery" type="search" class="form-control" placeholder="Search in key value data (keys and values)..." />
            </div>
          </div>
          <div class="col-sm-12 col-md-4 mt-2 mt-md-0">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="onlyMatches" v-model="showOnlyMatches">
              <label class="form-check-label" for="onlyMatches">Show only matching entries</label>
            </div>
          </div>
        </div>
        <div v-if="detailsLoading" class="d-flex justify-content-center align-items-center" style="min-height: 120px;">
          <b-spinner large></b-spinner>
        </div>
        <div v-else>
          <div v-if="detailsError" class="alert alert-danger">
            {{ detailsError }}
          </div>
          <template v-else-if="keyDetails">
            <div class="row mb-3">
              <div class="col-sm-12 col-md-3"><strong>Key:</strong> {{ keyDetails.key }}</div>
              <div class="col-sm-12 col-md-3"><strong>Type:</strong> {{ keyDetails.type }}</div>
              <div class="col-sm-12 col-md-3"><strong>TTL:</strong> {{ keyDetails.ttl }}</div>
              <div class="col-sm-12 col-md-3"><strong>Idle Time:</strong> {{ keyDetails.idle_time }}</div>
            </div>
            <hr/>
            <div>
              <h5 class="mb-2">Value</h5>
              <!--              <div class="mb-2">-->
              <!--                <button class="btn btn-sm btn-outline-primary" @click="rootExpanded = !rootExpanded">-->
              <!--                  <i class="uil" :class="rootExpanded ? 'uil-compress' : 'uil-expand-arrows'" />-->
              <!--                  {{ rootExpanded ? 'Collapse root' : 'Expand root' }}-->
              <!--                </button>-->
              <!--              </div>-->
              <div style="max-height: 480px; overflow: auto;">
                <TreeNode v-if="!showOnlyMatches" :node-val="detailsParsed || {}" :path="'$'" :search="searchQuery" />
                <TreeNode v-else :node-val="buildFilteredJson(detailsParsed || {}, searchQuery)" :path="'$'" :search="searchQuery" />
              </div>
            </div>
          </template>
          <div v-else class="text-center text-muted">
            <i class="uil mdi-database-cog font-size-24 me-2"></i>
            <span>No Key Data.</span>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>



